import { useEffect, useMemo, useRef, useState } from "react";
import { useInView, motion } from "motion/react";
import { ChannelData } from "./utils";
import { AnimVals } from '@/utils/animUtils';
import { toProxiedWpUrl } from '@/utils/wpMediaUrl';
import { usePostType } from "@/hooks/postType";
import { SplideSlide } from "react-splide-ts";
import { dedupeLineBreaks } from "@/core/utils";
import { Button, CursorFollower, RhLink, TypePill, HeadlineAnim, Image } from "@components";
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';

import className from "classnames/bind";
import styles from "./CarouselRockHallTv.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

type CarouselRockHallTvSlideProps = {
  canPlay: boolean;
  active: boolean;
  channel: ChannelData;
  noBorder: boolean;
  index: number;
  isRevealEnabled: boolean;
  isInView: boolean;
}

export default function CarouselRockHallTvSlide({
  canPlay,
  active,
  index,
  channel,
  isRevealEnabled,
  isInView,
  noBorder,
}: CarouselRockHallTvSlideProps) {
  const { title, excerpt, thumbnail, previewVideo, uri, linkTarget, labelOverride, pillLabel } = channel;
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const videoEl = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoInView = useInView(videoEl, { once: AnimVals.once, amount: 0.1 });
  const componentInView = useInView(containerRef, { once: AnimVals.once, amount: 0.1 });
  const isVideo = Boolean(previewVideo);
  const postType = usePostType({ node: { __typename: "RhtvChannel" } });
  const slideTitle = dedupeLineBreaks(title);
  const { labels } = usePageWrapperData();
  const ctaLabel = labelOverride || labels?.rhtvCarouselCta;
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // CursorFollower visibility control
  const [showCursorFollower, setShowCursorFollower] = useState(false);  // Track if slide has animated in (only animate once)
  const hasAnimatedIn = useRef(false);

  // -------------------- Animate in

  const effectiveIsInView = useMemo(() => {
    const shouldShow = isInView && active;
    return shouldShow;
  }, [isInView, active]);

  // Mark as animated once the animation should have completed
  useEffect(() => {
    if (effectiveIsInView && !hasAnimatedIn.current) {
      hasAnimatedIn.current = true;
    }
  }, [effectiveIsInView]);

  // Show cursor follower after slide is active and in view (desktop only)
  useEffect(() => {
    if (mounted && isReady && isDesktop && active && componentInView && uri) {
      setShowCursorFollower(true);
    } else {
      setShowCursorFollower(false);
    }
  }, [mounted, isReady, isDesktop, active, componentInView, uri]);

  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_OPACITY = AnimVals.opacity;
  const ANIM_EASE = AnimVals.ease;
  const ANIM_DUR = AnimVals.sm.duration;

  const createMotionProps = (delay: number = 0) => ({
    initial: isRevealEnabled ? {
      opacity: ANIM_OPACITY,
      y: ANIM_DIST
    } : {},
    animate: (effectiveIsInView || hasAnimatedIn.current) ? {
      opacity: 1,
      y: 0
    } : {},
    transition: {
      duration: ANIM_DUR,
      ease: ANIM_EASE,
      delay
    },
  });

  // -------------------- Video

  useEffect(() => {
    if (videoInView && isVideo && videoEl.current) {
      videoEl.current.src = previewVideo || "";
    };
  }, [videoInView, videoEl]);

  useEffect(() => {
    if (active && canPlay) {
      videoEl.current?.play();
    } else {
      videoEl.current?.pause();
    }
  }, [canPlay, active]);

  // -------------------- Render

  return (
    <SplideSlide className={cx(["slide", { 'no-border': noBorder }])} aria-label={slideTitle}>
      <RhLink
        className={cx("container")}
        href={uri ? uri : undefined}
        target={linkTarget ? linkTarget : undefined}
        ref={anchorRef}
      >

        <div className={cx("container-top")} ref={containerRef}>

          {isVideo ? (
            <video className={cx("video")}
              loop
              muted
              playsInline
              poster={toProxiedWpUrl(thumbnail?.node.mediaItemUrl)}
              ref={videoEl}
            />
          ) : (
            <Image className={cx("image")} image={thumbnail} />
          )}

          <div className={cx("container-top-content")}>

            {/* Pill */}
            { postType &&
              <motion.div {...createMotionProps(0)} key="pill">
                <TypePill className={cx("pill")}
                  type={postType.key}
                  copy={pillLabel || postType.label}
                />
              </motion.div>
            }

            {/* Headline */}
            <div className={cx("headline-wrapper")}>
              <HeadlineAnim
                tag="h3"
                text={slideTitle}
                className={cx(["display-xl", "slide-headline"])}
                isRevealEnabled={isRevealEnabled}
                isInView={effectiveIsInView || hasAnimatedIn.current}
                animInDelay={0.1}
                isCenterAlign={true}
                maxCharsPerWord={12}
              />
            </div>
          </div>

          {/* CTA */}
          <motion.div className={cx("learn-more")} {...createMotionProps(0.3)}>
            <Button label={ctaLabel} collapseOnMobile={true} asDiv={true} />
          </motion.div>

        </div>

        {/* Body */}
        {
          excerpt && (
            <motion.div className={cx("container-bottom")} {...createMotionProps(0.2)}>
              <p className={cx("text-m", "copy")} dangerouslySetInnerHTML={{ __html: excerpt }} />
            </motion.div>
          )
        }

        {/* CursorFollower */}
        {mounted && isReady && isDesktop && (
          <CursorFollower
            container={anchorRef}
            circleText={ctaLabel}
            isVisible={showCursorFollower}
            mouseActive={showCursorFollower}
          />
        )}

      </RhLink>
    </SplideSlide>
  );

}
